import type { z } from "zod";

import { activeBrand } from "../../shared/brand";
import { ApiErrorSchema, OpaqueIdSchema } from "../../shared/schemas";

export type ApiClientErrorOptions = {
  checkpointCount?: number | null;
  requestId?: string | null;
  retryable?: boolean | null;
};

export class ApiClientError extends Error {
  readonly checkpointCount: number | null;
  readonly code: string;
  readonly requestId: string | null;
  readonly retryable: boolean | null;
  readonly status: number;

  constructor(status: number, code: string, message: string, options: ApiClientErrorOptions = {}) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.code = code;
    this.requestId = options.requestId ?? null;
    this.checkpointCount = options.checkpointCount ?? null;
    this.retryable = options.retryable ?? null;
  }
}

export function validatedResponseRequestId(response: Response): string | null {
  const parsed = OpaqueIdSchema.safeParse(response.headers.get("X-Request-Id"));
  return parsed.success ? parsed.data : null;
}

export async function parseResponse<TSchema extends z.ZodType>(
  response: Response,
  schema: TSchema,
): Promise<z.output<TSchema>> {
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const parsedError = ApiErrorSchema.safeParse(payload);
    const bodyRequestId = parsedError.success ? (parsedError.data.error.requestId ?? null) : null;
    throw new ApiClientError(
      response.status,
      parsedError.success ? parsedError.data.error.code : "unexpected_error",
      parsedError.success
        ? parsedError.data.error.message
        : `${activeBrand.name} could not complete the request.`,
      { requestId: validatedResponseRequestId(response) ?? bodyRequestId },
    );
  }
  return schema.parse(payload);
}
